import type { BookingPaymentOverride } from "./paymentOverrides.js";
import { send } from "../api.js";

export type DiaryPaymentMethod =
  | "stripe"
  | "cash"
  | "card_terminal"
  | "bank_transfer"
  | "coupon"
  | "lesson_credit"
  | "on_site"
  | "complimentary"
  | "unknown";

export type DiaryPaymentStatus =
  | "not_required"
  | "pending"
  | "partially_paid"
  | "paid"
  | "failed"
  | "partially_refunded"
  | "refunded"
  | "unknown";

export interface DiaryPaymentClassification {
  appointment_id: number;
  booking_id?: number | null;
  payment_override?: BookingPaymentOverride | null;
  method: DiaryPaymentMethod;
  status: DiaryPaymentStatus;
  payment_id: number | null;
  amount_due: string | null;
  can_record_cancelled_payment?: boolean;
}

export type DiaryPaymentIndicatorTone =
  | "settled"
  | "due"
  | "overdue"
  | "attention"
  | "refund";

export interface DiaryPaymentIndicator {
  symbol: "✓" | "£" | "*" | "!" | "½" | "×" | "↩";
  label: string;
  tone: DiaryPaymentIndicatorTone;
}

export function diaryManualCreditUseAllowed(block: {
  manual_use?: { allowed: boolean };
}): boolean {
  // Compatibility with a temporarily older FastAPI deployment: retain the
  // selector behaviour that existed before the decision field was introduced.
  // The mutation endpoint remains authoritative for coupon-managed blocks.
  return block.manual_use?.allowed ?? true;
}

export function diaryManualCreditUseMessages(blocks: readonly {
  manual_use?: { allowed: boolean; reasons: Array<{ message: string }> };
}[]): string[] {
  return [
    ...new Set(
      blocks.flatMap((block) =>
        block.manual_use?.allowed === false
          ? block.manual_use.reasons.map((reason) => reason.message)
          : [],
      ),
    ),
  ];
}

const PAYMENT_CLASSIFICATION_BATCH_SIZE = 100;

export async function diaryPaymentClassifications(
  appointmentIds: readonly number[],
): Promise<DiaryPaymentClassification[]> {
  if (appointmentIds.length === 0) return [];

  const classifications: DiaryPaymentClassification[] = [];
  const batches: number[][] = [];
  for (let offset = 0; offset < appointmentIds.length; offset += PAYMENT_CLASSIFICATION_BATCH_SIZE) {
    batches.push(appointmentIds.slice(offset, offset + PAYMENT_CLASSIFICATION_BATCH_SIZE));
  }

  for (let offset = 0; offset < batches.length; offset += 2) {
    const rows = await Promise.all(
      batches.slice(offset, offset + 2).map((batch) =>
        send<DiaryPaymentClassification[]>(
          "POST",
          "/staff-api/api/v1/schedule/payment-classifications/query",
          { appointment_ids: batch },
        ),
      ),
    );
    classifications.push(...rows.flat());
  }

  return classifications;
}

/**
 * Existing settlement badge used by appointment action logic.
 *
 * Keep this deliberately narrow. It predates the richer Diary presentation
 * and is also used by code deciding whether a lesson is already funded. New
 * display states belong in `diaryPaymentIndicator`, not here.
 */
export function diaryPaymentBadge(
  payment: DiaryPaymentClassification | null | undefined,
): "£" | "*" | null {
  if (payment?.method === "lesson_credit" || payment?.method === "coupon") return "*";
  if (payment?.status === "paid") return "£";
  return null;
}

/**
 * Whether the Diary may open its "record payment / apply credit" panel.
 *
 * This intentionally mirrors the authoritative unpaid-lessons workflow: a
 * manual payment can only be recorded against a pending on-site payment. Do
 * not infer actionability from the absence of a paid badge — failed, refunded
 * and partially-paid are real states with their own resolution paths.
 *
 * The panel itself still checks for a payment id and amount before offering a
 * money-recording button. Keeping those checks there means an otherwise valid
 * pending on-site lesson can still use an existing lesson credit even if the
 * payment row is temporarily incomplete.
 */
export function diaryPaymentCanResolveOnSite(
  payment: DiaryPaymentClassification | null | undefined,
): payment is DiaryPaymentClassification {
  return payment?.status === "pending" && payment.method === "on_site";
}

function lessonHasPassed(lessonEndsAt: string | null | undefined, now: Date): boolean {
  if (!lessonEndsAt) return false;
  const end = new Date(lessonEndsAt);
  return Number.isFinite(end.getTime()) && end.getTime() < now.getTime();
}

/**
 * Human-readable source for a settled payment.
 *
 * Keep the source as specific as the booking service actually proves. In
 * particular, legacy `on_site` means Amelia knows it was handled on site but
 * does not prove cash versus card, so say "On-site" rather than guessing.
 */
export function diaryPaymentSourceLabel(
  payment: DiaryPaymentClassification | null | undefined,
): string | null {
  if (payment?.payment_override) return "Override";
  switch (payment?.method) {
    case "stripe":
      return "Stripe";
    case "cash":
      return "Cash";
    case "card_terminal":
      return "Card machine";
    case "bank_transfer":
      return "Bank transfer";
    case "coupon":
      return "Coupon";
    case "lesson_credit":
      return "Lesson credit";
    case "on_site":
      return "On-site";
    case "complimentary":
      return "Complimentary";
    case "unknown":
    case undefined:
      return null;
  }
}

/** Canonical staff-facing label for the booking service's eight payment states. */
export function diaryPaymentStateLabel(
  payment: DiaryPaymentClassification | null | undefined,
  lessonEndsAt?: string | null,
  now: Date = new Date(),
): string | null {
  if (!payment) return null;

  switch (payment.status) {
    case "not_required":
      return payment.method === "lesson_credit" ? "Paid · Lesson credit" : "No payment required";
    case "pending":
      return lessonHasPassed(lessonEndsAt, now) ? "Overdue" : "Due";
    case "partially_paid":
      return "Part paid";
    case "paid": {
      const method = diaryPaymentSourceLabel(payment);
      return method ? `Paid · ${method}` : "Paid";
    }
    case "failed":
      return "Failed";
    case "partially_refunded":
      return "Part refunded";
    case "refunded":
      return "Refunded";
    case "unknown":
      return "Unknown";
  }
}

/**
 * Compact Diary-only marker derived from the real payment status.
 *
 * Lesson credit remains a funding method, not a status: the backend represents
 * an active lesson-credit settlement as `lesson_credit + not_required`, which
 * still gets the familiar `*` glyph. Only `lesson_credit` is special-cased here
 * — a coupon-funded lesson arrives as `coupon + paid` and settles through the
 * normal `paid` path. Other no-payment-required classifications deliberately
 * have no marker. Unknown also has no tile marker: a visible `?` can contradict
 * a later successful live read in the drawer, whereas no marker accurately
 * means that the compact Diary cannot currently assert a payment state.
 */
export function diaryPaymentIndicator(
  payment: DiaryPaymentClassification | null | undefined,
  lessonEndsAt?: string | null,
  now: Date = new Date(),
): DiaryPaymentIndicator | null {
  if (!payment) return null;
  if (payment.payment_override && payment.status === "paid") {
    return { symbol: "✓", label: "Paid · Override", tone: "settled" };
  }
  if (payment.method === "lesson_credit" && payment.status === "not_required") {
    return { symbol: "*", label: "Lesson credit", tone: "settled" };
  }
  if (payment.status === "not_required") return null;

  const label = diaryPaymentStateLabel(payment, lessonEndsAt, now);
  if (!label) return null;

  switch (payment.status) {
    case "pending":
      return {
        symbol: "!",
        label,
        tone: lessonHasPassed(lessonEndsAt, now) ? "overdue" : "due",
      };
    case "partially_paid":
      return { symbol: "½", label, tone: "due" };
    case "paid":
      return {
        symbol: payment.method === "lesson_credit" || payment.method === "coupon" ? "*" : "£",
        label,
        tone: "settled",
      };
    case "failed":
      return { symbol: "×", label, tone: "attention" };
    case "partially_refunded":
    case "refunded":
      return { symbol: "↩", label, tone: "refund" };
    case "unknown":
      return null;
  }
}

export async function useExistingLessonCredit(
  lessonBlockId: string,
  input: {
    idempotency_key: string;
    amelia_appointment_id: number;
    portal_student_id: string;
    notes: string;
  },
): Promise<unknown> {
  return send(
    "POST",
    `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}/credits/use`,
    input,
  );
}
